import type { CSSProperties } from "react";

import { ColorType } from "@/theme/colors";
import { FontWeight } from "@/theme/typography";
import { pxToRem } from "@/theme/spacing";
import type { DropdownOptionViewState } from "./option-state";
import {
  DROPDOWN_TEXT_SHADOW,
  DROPDOWN_TRANSFORM,
  DROPDOWN_TRANSFORM_ORIGIN,
} from "./constants";

interface OptionAnimationInput {
  viewState: DropdownOptionViewState;
  isAnimating: boolean;
  isClosing: boolean;
}

export function buildOptionHighlightStyle(
  viewState: DropdownOptionViewState,
): CSSProperties {
  return {
    position: "absolute",
    top: 0,
    left: 0,
    right: 0,
    bottom: 0,
    borderRadius: "25px",
    backgroundImage: "linear-gradient(90deg, rgba(102, 254, 193, 0.1) 0%, rgba(102, 254, 193, 0.02) 100%)",
    opacity: viewState.isHighlighted ? 1 : 0,
    transform: viewState.isHighlighted ? "scaleX(1)" : "scaleX(0.95)",
    transformOrigin: DROPDOWN_TRANSFORM_ORIGIN.Left,
    transition: "all 250ms cubic-bezier(0.2, 0.8, 0.2, 1)",
    zIndex: 0,
    boxShadow: viewState.isHighlighted
      ? "rgba(102, 254, 193, 0.4) 2px 0px 3px 0px inset, rgba(255, 255, 255, 0.5) 1px 1px 2px 0px inset"
      : "none",
  };
}

export function buildOptionButtonStyle(
  input: OptionAnimationInput,
): CSSProperties {
  return {
    padding: `${pxToRem(14)} ${pxToRem(20)}`,
    display: "flex",
    alignItems: "center",
    justifyContent: "space-between",
    gap: 16,
    opacity: resolveOptionButtonOpacity(input),
    transform: resolveOptionButtonTransform(input),
    transition: `
                            transform 350ms cubic-bezier(0.2, 0.8, 0.2, 1) ${input.viewState.animationDelay}ms,
                            opacity 250ms ease-out ${input.viewState.animationDelay}ms
                          `,
    pointerEvents: input.isClosing ? "none" : "auto",
    position: "relative",
    zIndex: 1,
  };
}

export function buildOptionIconFrameStyle(
  viewState: DropdownOptionViewState,
): CSSProperties {
  return {
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
    width: 28,
    height: 28,
    borderRadius: "50%",
    backgroundColor: viewState.isHighlighted ? "rgba(102, 254, 193, 0.1)" : "transparent",
    backgroundImage: "none",
    transition: "all 250ms cubic-bezier(0.2, 0.8, 0.2, 1)",
    transform: viewState.isHighlighted ? "scale(1.1)" : "scale(1)",
  };
}

export function buildOptionLabelStyle(
  viewState: DropdownOptionViewState,
): CSSProperties {
  return {
    whiteSpace: "nowrap",
    letterSpacing: viewState.isHighlighted ? "0.02em" : "0.01em",
    transition: "all 250ms cubic-bezier(0.2, 0.8, 0.2, 1)",
    textShadow: viewState.isHighlighted
      ? DROPDOWN_TEXT_SHADOW.Highlighted
      : DROPDOWN_TEXT_SHADOW.None,
  };
}

export function buildOptionHoverIndicatorStyle(
  isHighlighted: boolean,
): CSSProperties {
  return {
    opacity: isHighlighted ? 1 : 0,
    transform: isHighlighted ? "translateX(0)" : "translateX(-10px)",
    transition: "all 250ms cubic-bezier(0.2, 0.8, 0.2, 1)",
    color: "var(--color-accent)",
    fontSize: "12px",
    display: "flex",
    alignItems: "center",
    textShadow: "0 0 8px rgba(102, 254, 193, 0.6)",
  };
}

export function resolveOptionIconColor(
  viewState: DropdownOptionViewState,
): ColorType {
  return viewState.isHighlighted ? ColorType.Accent : ColorType.White;
}

export function resolveOptionIconOpacity(
  viewState: DropdownOptionViewState,
): number {
  if (viewState.isDisabled) {
    return 0.4;
  }

  return viewState.isHighlighted ? 1 : 0.6;
}

export function resolveOptionLabelWeight(
  viewState: DropdownOptionViewState,
): FontWeight {
  return viewState.isHighlighted ? FontWeight.Medium : FontWeight.Light;
}

function resolveOptionButtonOpacity(input: OptionAnimationInput): number {
  if (!input.isAnimating || input.isClosing) {
    return 0;
  }

  return input.viewState.isDisabled ? 0.3 : 1;
}

function resolveOptionButtonTransform(input: OptionAnimationInput): string {
  if (!input.isAnimating || input.isClosing) {
    return DROPDOWN_TRANSFORM.ItemClosed;
  }

  return input.viewState.isHoveredByMouse
    ? DROPDOWN_TRANSFORM.ItemHovered
    : DROPDOWN_TRANSFORM.ItemIdle;
}
